import type {
  BorderColorModifierValue,
  BorderStyleModifierValue,
  BorderWidthModifierValue,
} from "schemas/generated/fileModifiers";

import React from "react";

import { useSemanticModifierValue } from "@agent-ui/features/modifiers/hooks/useSemanticModifierValue";
import {
  parseColorValue,
  parsePixelValue,
} from "@agent-ui/features/modifiers/utils/parseComputedValue";
import { COMPUTE_VALUE } from "schemas/fileModifiers";

import BorderColorModifier from "./BorderColorModifier";
import BorderStyleModifier from "./BorderStyleModifier";
import BorderWidthModifier from "./BorderWidthModifier";

interface BorderModifierProps {
  onChange: (
    modifiers: (
      | BorderColorModifierValue
      | BorderStyleModifierValue
      | BorderWidthModifierValue
    )[],
  ) => void;
}

const BorderModifier: React.FC<BorderModifierProps> = ({ onChange }) => {
  const currentBorderTopWidth =
    useSemanticModifierValue({
      semanticExtractor: ({ computedStyles }) => {
        return parsePixelValue(computedStyles.borderTopWidth) ?? null;
      },
    }) ?? null;
  const currentBorderRightWidth =
    useSemanticModifierValue({
      semanticExtractor: ({ computedStyles }) => {
        return parsePixelValue(computedStyles.borderRightWidth) ?? null;
      },
    }) ?? null;
  const currentBorderBottomWidth =
    useSemanticModifierValue({
      semanticExtractor: ({ computedStyles }) => {
        return parsePixelValue(computedStyles.borderBottomWidth) ?? null;
      },
    }) ?? null;
  const currentBorderLeftWidth =
    useSemanticModifierValue({
      semanticExtractor: ({ computedStyles }) => {
        return parsePixelValue(computedStyles.borderLeftWidth) ?? null;
      },
    }) ?? null;

  const currentBorderColor =
    useSemanticModifierValue({
      semanticExtractor: ({ computedStyles }) => {
        return parseColorValue(computedStyles.borderTopColor);
      },
    }) ?? null;

  const currentBorderStyle =
    useSemanticModifierValue({
      semanticExtractor: ({ computedStyles }) => {
        return computedStyles.borderTopStyle || null;
      },
    }) ?? null;

  const handleColorChange = React.useCallback(
    (modifier: BorderColorModifierValue) => {
      onChange([modifier]);
    },
    [onChange],
  );

  const handleStyleChange = React.useCallback(
    (modifier: BorderStyleModifierValue) => {
      onChange([modifier]);
    },
    [onChange],
  );

  const handleWidthChange = React.useCallback(
    (modifiers: BorderWidthModifierValue[]) => {
      const allCurrentWidthsZero = [
        currentBorderTopWidth,
        currentBorderRightWidth,
        currentBorderBottomWidth,
        currentBorderLeftWidth,
      ].every((w) => w === null || w === 0);

      const hasNonZeroNewWidth = modifiers.some(
        (m) => m.value !== null && m.value !== 0,
      );

      if (allCurrentWidthsZero && hasNonZeroNewWidth) {
        const additionalModifiers: (
          | BorderColorModifierValue
          | BorderStyleModifierValue
        )[] = [];

        const hasVisibleColorOverride =
          currentBorderColor && currentBorderColor !== "transparent";
        if (!hasVisibleColorOverride) {
          additionalModifiers.push({
            type: "borderColor",
            value: COMPUTE_VALUE,
          });
        }

        const hasVisibleStyleOverride =
          currentBorderStyle && currentBorderStyle !== "none";
        if (!hasVisibleStyleOverride) {
          additionalModifiers.push({
            type: "borderStyle",
            value: "solid",
          });
        }

        if (additionalModifiers.length > 0) {
          onChange([...modifiers, ...additionalModifiers]);
        } else {
          onChange(modifiers);
        }
      } else {
        onChange(modifiers);
      }
    },
    [
      onChange,
      currentBorderTopWidth,
      currentBorderRightWidth,
      currentBorderBottomWidth,
      currentBorderLeftWidth,
      currentBorderColor,
      currentBorderStyle,
    ],
  );

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 items-start">
        <BorderColorModifier onChange={handleColorChange} />
        <BorderStyleModifier onChange={handleStyleChange} />
      </div>
      <BorderWidthModifier onChange={handleWidthChange} />
    </div>
  );
};

export default BorderModifier;
